import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { defaultImage } from '../../settings/config';
import { Pencil, Eye, Trash2, X } from 'lucide-react';

// Rotating accent colors for visual variety across cards
const accentColors = [
  { gradient: 'from-primary via-primary/80 to-accent', ring: 'ring-primary/30', bg: 'bg-primary' },
  { gradient: 'from-accent via-accent/80 to-secondary', ring: 'ring-accent/30', bg: 'bg-accent' },
  { gradient: 'from-secondary via-secondary/80 to-primary', ring: 'ring-secondary/30', bg: 'bg-secondary' },
  { gradient: 'from-primary via-accent to-secondary', ring: 'ring-accent/30', bg: 'bg-accent' },
];

interface ChildCardProps {
  child: any;
  deleteAction: (id: string) => void;
  index?: number;
}

function ChildCard({ child, deleteAction, index = 0 }: ChildCardProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const colors = accentColors[index % accentColors.length];

  const confirmDelete = (event: React.MouseEvent) => {
    event.preventDefault();
    setShowConfirm(true);
  };

  const handleDelete = () => {
    deleteAction(child.id);
    setShowConfirm(false);
  };

  return (
    <>
      <div className="group relative rounded-2xl overflow-hidden bg-card border border-border/40 shadow-soft-sm hover:shadow-soft-lg transition-all duration-300 hover:-translate-y-1.5">
        {/* Colored accent bar at top — unique per card */}
        <div className={`h-1.5 bg-gradient-to-r ${colors.gradient}`} />

        {/* Profile-style card layout */}
        <div className="relative p-6 pb-4">
          {/* Background decoration */}
          <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-muted/30 to-transparent dark:from-muted/10" />

          {/* Centered avatar with gradient ring */}
          <Link to={`/children/${child.id}`} className="relative z-10 flex flex-col items-center no-underline">
            <div className="relative mb-4">
              <div className={`absolute -inset-1 rounded-full bg-gradient-to-tr ${colors.gradient} opacity-40 blur-sm group-hover:opacity-60 transition-opacity duration-300`} />
              <div className="relative w-24 h-24 rounded-full overflow-hidden border-[3px] border-card shadow-soft-md group-hover:shadow-soft-lg transition-shadow duration-300">
                <img
                  alt={`${child.first_name} profile`}
                  src={child.image_url || defaultImage}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
            </div>

            {/* Name */}
            <h3 className="font-bold text-lg text-card-foreground group-hover:text-primary transition-colors duration-200 text-center">
              {child.first_name}
            </h3>

            {/* Bio excerpt */}
            {child.bio ? (
              <p className="text-muted-foreground text-sm mt-1.5 text-center line-clamp-2 leading-relaxed max-w-[220px]">
                {child.bio}
              </p>
            ) : (
              <p className="text-muted-foreground/50 text-xs mt-1.5 italic">No bio yet</p>
            )}
          </Link>
        </div>

        {/* Action buttons — icon pills */}
        <div className="flex items-center justify-center gap-1.5 px-4 pb-4 pt-1">
          <Link
            to={`/children/${child.id}/edit`}
            className="p-2.5 rounded-xl text-muted-foreground hover:text-accent hover:bg-accent/10 transition-all duration-200 no-underline"
            title="Edit"
            aria-label="Edit child"
          >
            <Pencil className="w-4 h-4" />
          </Link>
          <Link
            to={`/preview/${child.id}`}
            target="_blank"
            className="p-2.5 rounded-xl text-muted-foreground hover:text-secondary hover:bg-secondary/10 transition-all duration-200 no-underline"
            title="Preview"
            aria-label="Preview child profile"
          >
            <Eye className="w-4 h-4" />
          </Link>
          <button
            onClick={confirmDelete}
            className="p-2.5 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-200"
            title="Delete"
            aria-label="Delete child"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {showConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShowConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-card rounded-2xl p-6 mx-4 max-w-sm w-full shadow-soft-xl border border-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-destructive" />
                </div>
                <button
                  onClick={() => setShowConfirm(false)}
                  className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <h3 className="font-semibold text-lg text-card-foreground mb-2">Delete {child.first_name}?</h3>
              <p className="text-sm text-muted-foreground mb-6">
                This will permanently remove their profile. Any cards linked to this child will no longer work as expected.
              </p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowConfirm(false)} className="btn-light px-4 py-2.5 rounded-xl">
                  Cancel
                </button>
                <button onClick={handleDelete} className="btn-danger px-4 py-2.5 rounded-xl">
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default ChildCard;
